/**
 * RoleAwareDashboard — Generic role-aware dashboard that shows different widgets
 * based on user role. Includes:
 *  - Role-based KPI cards
 *  - Resume Work widget (optional, manual)
 *  - Today's tasks summary
 *  - Pending approvals summary
 *  - Recent notifications
 *  - Quick action cards
 * 
 * Every login lands here — fresh start, no auto-restore.
 */
import React, { useState, useEffect } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, PieChart, Pie, Cell 
} from 'recharts';
import { 
  DollarSign, Package, Users, TrendingUp, ArrowUpRight, ArrowDownRight,
  Bell, CheckCircle2, Clock, AlertTriangle, FileText, ShoppingCart,
  Briefcase, Building2, Calendar, Target, Zap, ChevronRight,
  Activity, PieChart as PieChartIcon, BarChart2, Loader2
} from 'lucide-react';
import { useERPContextSafe } from '@/context/ERPContext';
import { useGlobal } from '@/store/GlobalStoreProvider';
import { GK } from '@/store/GlobalKeys';
import { API_BASE, getToken } from '@/lib/api';
import ResumeWorkWidget from '@/components/ResumeWorkWidget';

// ═══════════════════════════════════════════════════════════════════════════
// ROLE-BASED KPI CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════
const ROLE_KPI_CONFIG = {
  // Admin Dashboard KPIs
  ADMIN: [
    { key: 'revenue', label: 'Total Revenue', icon: DollarSign, accent: 'emerald', format: 'currency' },
    { key: 'orders', label: 'Orders Today', icon: ShoppingCart, accent: 'blue', format: 'number' },
    { key: 'customers', label: 'Active Customers', icon: Users, accent: 'purple', format: 'number' },
    { key: 'growth', label: 'Growth Rate', icon: TrendingUp, accent: 'amber', format: 'percent' },
  ],
  SUPER_ADMIN: [
    { key: 'revenue', label: 'Total Revenue', icon: DollarSign, accent: 'emerald', format: 'currency' },
    { key: 'branches', label: 'Active Branches', icon: Building2, accent: 'blue', format: 'number' },
    { key: 'users', label: 'Total Users', icon: Users, accent: 'purple', format: 'number' },
    { key: 'alerts', label: 'System Alerts', icon: AlertTriangle, accent: 'red', format: 'number' },
  ],
  // Store Manager Dashboard
  MANAGER: [
    { key: 'sales', label: "Today's Sales", icon: DollarSign, accent: 'emerald', format: 'currency' },
    { key: 'inventory', label: 'Low Stock Items', icon: Package, accent: 'orange', format: 'number' },
    { key: 'footfall', label: 'Customer Footfall', icon: Users, accent: 'blue', format: 'number' },
    { key: 'targets', label: 'Target Achievement', icon: Target, accent: 'indigo', format: 'percent' },
  ],
  // Cashier/POS Dashboard
  CASHIER: [
    { key: 'bills', label: 'Bills Today', icon: FileText, accent: 'blue', format: 'number' },
    { key: 'amount', label: 'Amount Collected', icon: DollarSign, accent: 'emerald', format: 'currency' },
    { key: 'returns', label: 'Returns', icon: ArrowUpRight, accent: 'orange', format: 'number' },
    { key: 'pending', label: 'Pending Bills', icon: Clock, accent: 'amber', format: 'number' },
  ],
  // HR Dashboard
  HR: [
    { key: 'employees', label: 'Total Employees', icon: Users, accent: 'blue', format: 'number' },
    { key: 'attendance', label: 'Present Today', icon: CheckCircle2, accent: 'emerald', format: 'number' },
    { key: 'leaves', label: 'Pending Leaves', icon: Calendar, accent: 'amber', format: 'number' },
    { key: 'approvals', label: 'Pending Approvals', icon: Clock, accent: 'purple', format: 'number' },
  ],
  // Finance Dashboard
  FINANCE: [
    { key: 'receivables', label: 'Receivables', icon: ArrowUpRight, accent: 'emerald', format: 'currency' },
    { key: 'payables', label: 'Payables', icon: ArrowDownRight, accent: 'red', format: 'currency' },
    { key: 'cashflow', label: 'Cash Flow', icon: Activity, accent: 'blue', format: 'currency' },
    { key: 'overdue', label: 'Overdue Invoices', icon: AlertTriangle, accent: 'amber', format: 'number' },
  ],
  // Default for other roles
  DEFAULT: [
    { key: 'tasks', label: 'My Tasks', icon: CheckCircle2, accent: 'blue', format: 'number' },
    { key: 'notifications', label: 'Notifications', icon: Bell, accent: 'amber', format: 'number' },
    { key: 'recent', label: 'Recent Activity', icon: Clock, accent: 'purple', format: 'number' },
    { key: 'approvals', label: 'Pending Items', icon: FileText, accent: 'emerald', format: 'number' },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// KPI CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const KPICard = ({ kpi, value, trend, isDark, currencySymbol = '₹', loading = false }) => {
  const accentMap = {
    emerald: { icon: isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600' },
    orange: { icon: isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-600' },
    blue: { icon: isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600' },
    indigo: { icon: isDark ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600' },
    purple: { icon: isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600' },
    amber: { icon: isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-600' },
    red: { icon: isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600' },
  };

  const formatValue = (val, format) => {
    if (loading) return '—';
    if (val === null || val === undefined) return '—';
    switch (format) {
      case 'currency': return `${currencySymbol}${Number(val).toLocaleString()}`;
      case 'percent': return `${val}%`;
      case 'number': return Number(val).toLocaleString();
      default: return val;
    }
  };

  const Icon = kpi.icon;

  return (
    <div 
      data-testid={`kpi-card-${kpi.key}`}
      className={`p-4 rounded-xl border transition-all hover:shadow-md ${
        isDark ? 'bg-[#151515] border-[#222] hover:border-[#333]' : 'bg-white border-slate-200 shadow-sm hover:border-slate-300'
      }`}
    >
      <div className="flex justify-between items-start mb-3">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          {kpi.label}
        </span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accentMap[kpi.accent]?.icon || accentMap.blue.icon}`}>
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Icon size={18} />}
        </div>
      </div>
      <h3 className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
        {formatValue(value, kpi.format)}
      </h3>
      {trend && (
        <div className={`text-[10px] font-bold flex items-center gap-0.5 mt-1 ${
          trend.up ? 'text-emerald-500' : 'text-rose-500'
        }`}>
          {trend.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {trend.value}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// QUICK ACTION CARD
// ═══════════════════════════════════════════════════════════════════════════
const QuickActionCard = ({ icon: Icon, label, description, onClick, isDark, color = 'blue' }) => {
  const colorMap = {
    blue: isDark ? 'hover:border-blue-500/50 hover:bg-blue-500/5' : 'hover:border-blue-300 hover:bg-blue-50/50',
    emerald: isDark ? 'hover:border-emerald-500/50 hover:bg-emerald-500/5' : 'hover:border-emerald-300 hover:bg-emerald-50/50',
    amber: isDark ? 'hover:border-amber-500/50 hover:bg-amber-500/5' : 'hover:border-amber-300 hover:bg-amber-50/50',
    purple: isDark ? 'hover:border-purple-500/50 hover:bg-purple-500/5' : 'hover:border-purple-300 hover:bg-purple-50/50',
  };

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl border text-left transition-all group ${
        isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200'
      } ${colorMap[color]}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${
        isDark ? 'bg-white/5 text-slate-400 group-hover:text-white' : 'bg-slate-100 text-slate-500 group-hover:text-slate-700'
      }`}>
        <Icon size={20} />
      </div>
      <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{label}</h4>
      <p className={`text-[10px] mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{description}</p>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICATION ITEM
// ═══════════════════════════════════════════════════════════════════════════
const NotificationItem = ({ notification, isDark }) => (
  <div className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
    isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
  }`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
      notification.type === 'alert' ? 'bg-red-500/10 text-red-500' :
      notification.type === 'success' ? 'bg-emerald-500/10 text-emerald-500' :
      notification.type === 'warning' ? 'bg-amber-500/10 text-amber-500' :
      'bg-blue-500/10 text-blue-500'
    }`}>
      {notification.type === 'alert' ? <AlertTriangle size={14} /> :
       notification.type === 'success' ? <CheckCircle2 size={14} /> :
       notification.type === 'warning' ? <Clock size={14} /> :
       <Bell size={14} />}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-xs font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
        {notification.title}
      </p>
      <p className={`text-[10px] mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        {notification.time}
      </p>
    </div>
    <ChevronRight size={14} className={isDark ? 'text-slate-600' : 'text-slate-300'} />
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const RoleAwareDashboard = ({ theme = 'light', onNavigate }) => {
  const isDark = theme === 'dark';
  const erpCtx = useERPContextSafe();
  
  // Get user role from GlobalStore
  const userRole = useGlobal(GK.USER_ROLE, erpCtx.role || 'DEFAULT');
  const userName = useGlobal(GK.USER_NAME, erpCtx.username || 'User');
  const companyName = useGlobal(GK.COMPANY_NAME, '');
  const branchName = useGlobal(GK.BRANCH_NAME, '');
  const currencySymbol = useGlobal(GK.CURRENCY_SYMBOL, '₹');
  const businessDate = useGlobal(GK.BUSINESS_DATE_FMT, new Date().toLocaleDateString());
  
  // State
  const [kpiData, setKpiData] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determine which KPI config to use based on role
  const getRoleKPIs = () => {
    const role = (userRole || '').toUpperCase();
    if (role.includes('SUPER') || role.includes('ADMIN')) return ROLE_KPI_CONFIG.SUPER_ADMIN;
    if (role.includes('MANAGER')) return ROLE_KPI_CONFIG.MANAGER;
    if (role.includes('CASHIER') || role.includes('POS')) return ROLE_KPI_CONFIG.CASHIER;
    if (role.includes('HR')) return ROLE_KPI_CONFIG.HR;
    if (role.includes('FINANCE') || role.includes('ACCOUNT')) return ROLE_KPI_CONFIG.FINANCE;
    return ROLE_KPI_CONFIG.DEFAULT;
  };

  const kpis = getRoleKPIs();

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, [userRole]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setKpiData(data.kpis || {});
        setNotifications(data.notifications || []);
      } else {
        // Use mock data if endpoint doesn't exist
        setKpiData({
          revenue: 145600,
          orders: 127,
          customers: 1284,
          growth: 24.8,
          branches: 8,
          users: 45,
          alerts: 2,
          sales: 89500,
          inventory: 12,
          footfall: 342,
          targets: 87,
          bills: 89,
          amount: 125000,
          returns: 3,
          pending: 2,
          employees: 156,
          attendance: 142,
          leaves: 5,
          approvals: 8,
          receivables: 450000,
          payables: 280000,
          cashflow: 170000,
          overdue: 15,
          tasks: 5,
          notifications: 3,
          recent: 12,
        });
        setNotifications([
          { id: 1, title: 'Low stock alert: SKU-1234', type: 'warning', time: '5 min ago' },
          { id: 2, title: 'New order received #ORD2024001', type: 'info', time: '15 min ago' },
          { id: 3, title: 'Payment received from ABC Corp', type: 'success', time: '1 hour ago' },
        ]);
      }
    } catch (e) {
      console.warn('[Dashboard] Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  // Chart data (mock)
  const revenueData = [
    { name: 'Mon', rev: 4000 },
    { name: 'Tue', rev: 3000 },
    { name: 'Wed', rev: 5000 },
    { name: 'Thu', rev: 2780 },
    { name: 'Fri', rev: 4890 },
    { name: 'Sat', rev: 6390 },
    { name: 'Sun', rev: 3490 },
  ];

  const strokeColor = isDark ? '#1e293b' : '#e2e8f0';
  const labelColor = isDark ? '#64748b' : '#94a3b8';

  return (
    <div 
      data-testid="role-aware-dashboard" 
      className={`h-full flex flex-col overflow-auto scrollbar-hide ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#f5f6f8]'}`}
    >
      <div className="p-5 space-y-5">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className={`text-xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Welcome back, {userName}
            </h1>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
              {companyName && `${companyName} • `}{branchName && `${branchName} • `}{businessDate}
            </p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase ${
            isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
          }`}>
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            System Online
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map((kpi) => (
            <KPICard
              key={kpi.key}
              kpi={kpi}
              value={kpiData[kpi.key]}
              trend={kpi.key === 'growth' ? { up: true, value: '+12.5% from last month' } : null}
              isDark={isDark}
              currencySymbol={currencySymbol}
              loading={loading}
            />
          ))}
        </div>

        {/* ── Main Content Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Chart */}
          <div className={`lg:col-span-2 p-5 rounded-xl border ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Weekly Performance
              </h3>
              <div className="flex items-center gap-2">
                <button className={`p-1.5 rounded-lg ${isDark ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                  <BarChart2 size={14} />
                </button>
                <button className={`p-1.5 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                  <Activity size={14} />
                </button>
              </div>
            </div>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={strokeColor} vertical={false} />
                  <XAxis dataKey="name" stroke={labelColor} fontSize={10} fontWeight={600} tickLine={false} axisLine={false} />
                  <YAxis stroke={labelColor} fontSize={10} fontWeight={600} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDark ? '#1e293b' : '#fff', 
                      border: isDark ? '1px solid #334155' : '1px solid #e2e8f0', 
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '600'
                    }} 
                  />
                  <Area type="monotone" dataKey="rev" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorRev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Notifications Panel */}
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Notifications
              </h3>
              <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                {notifications.length} new
              </span>
            </div>
            <div className="space-y-1">
              {notifications.slice(0, 5).map(notification => (
                <NotificationItem key={notification.id} notification={notification} isDark={isDark} />
              ))}
            </div>
            {notifications.length > 5 && (
              <button className={`w-full mt-3 py-2 text-[10px] font-bold uppercase rounded-lg transition-colors ${
                isDark ? 'text-blue-400 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50'
              }`}>
                View All Notifications
              </button>
            )}
          </div>
        </div>

        {/* ── Resume Work Widget + Quick Actions ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Resume Work Widget */}
          <ResumeWorkWidget 
            theme={theme} 
            onNavigate={onNavigate}
            className="lg:col-span-1"
          />

          {/* Quick Actions */}
          <div className={`lg:col-span-2 p-4 rounded-xl border ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-[10px] font-black uppercase tracking-widest mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <QuickActionCard
                icon={ShoppingCart}
                label="New Sale"
                description="Create POS bill"
                onClick={() => onNavigate?.('POS_BILLING')}
                isDark={isDark}
                color="emerald"
              />
              <QuickActionCard
                icon={FileText}
                label="Invoice"
                description="Sales invoice"
                onClick={() => onNavigate?.('SALES_INVOICE')}
                isDark={isDark}
                color="blue"
              />
              <QuickActionCard
                icon={Package}
                label="Stock"
                description="Check inventory"
                onClick={() => onNavigate?.('CLOSING_STOCK')}
                isDark={isDark}
                color="amber"
              />
              <QuickActionCard
                icon={Users}
                label="Customers"
                description="Manage customers"
                onClick={() => onNavigate?.('CUSTOMER_MASTER')}
                isDark={isDark}
                color="purple"
              />
            </div>
          </div>
        </div>

        {/* ── Today's Tasks (role-aware) ── */}
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Today's Priority Tasks
            </h3>
            <button className={`text-[10px] font-bold uppercase ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              View All
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { title: 'Review pending purchase orders', priority: 'high', module: 'Procurement' },
              { title: 'Approve inventory adjustments', priority: 'medium', module: 'Inventory' },
              { title: 'Generate monthly sales report', priority: 'low', module: 'Reports' },
            ].map((task, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                  isDark ? 'bg-[#0e0e10] border-[#222] hover:border-[#333]' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {task.title}
                  </p>
                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase shrink-0 ${
                    task.priority === 'high' ? 'bg-red-500/10 text-red-500' :
                    task.priority === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                    'bg-blue-500/10 text-blue-500'
                  }`}>
                    {task.priority}
                  </span>
                </div>
                <p className={`text-[10px] mt-1 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  {task.module}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleAwareDashboard;
